import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type {
  AgendaItem,
  AgendaItemList,
  AgendaItemSignalState,
  AgendaItemStatus,
} from "@elation/api-client/src/stitch-web";
import {
  type CreateAgendaItemMutationError,
  type DeleteAgendaItemMutationError,
  getListAgendaItemsQueryKey,
  type UpdateAgendaItemMutationError,
  useCreateAgendaItem,
  useDeleteAgendaItem,
  useUpdateAgendaItem,
} from "@elation/api-client/src/stitch-web";

import { usePracticeId } from "@src/features/practices";

let tempIdCounter = 0;

export type PendingItemId = number & { readonly __brand: "pending-item-id" };

const mintPendingId = (): PendingItemId => -++tempIdCounter as PendingItemId;

export const isPendingAgendaItem = (id: number): id is PendingItemId => id < 0;

type CachedAgendaItemList = { response?: unknown; data: AgendaItemList };

function getOptimisticAgendaItemPatch(data: {
  status?: AgendaItemStatus;
}): Partial<AgendaItem> {
  if (data.status !== "open") return data;

  return {
    ...data,
    signalState: "open",
    completedAt: null,
    completedBy: undefined,
    discussedAt: null,
    assessedAt: null,
    plannedAt: null,
    followUpAt: null,
  };
}

export type AgendaItemMutationVerb = "create" | "update" | "delete";

export interface AgendaItemMutationError {
  verb: AgendaItemMutationVerb;
  status: number | null;
}

export interface UseAgendaItemMutationsParams {
  encounterId: number | undefined;
  patientId: number | undefined;
}

export interface UseAgendaItemMutationsResult {
  createAgendaItem: (description: string) => Promise<void>;
  toggleAgendaItem: (
    itemId: number,
    currentStatus: AgendaItemStatus,
    currentSignalState: AgendaItemSignalState,
  ) => Promise<void>;
  deleteAgendaItem: (itemId: number) => Promise<void>;
  error: AgendaItemMutationError | null;
  pendingCreateIds: PendingItemId[];
  completedCreateIds: Map<PendingItemId, number>;
}

export function useAgendaItemMutations({
  encounterId,
  patientId,
}: UseAgendaItemMutationsParams): UseAgendaItemMutationsResult {
  const practiceId = usePracticeId();
  const queryClient = useQueryClient();
  const [error, setError] = useState<AgendaItemMutationError | null>(null);
  const [pendingCreateIds, setPendingCreateIds] = useState<PendingItemId[]>([]);
  const [completedCreateIds, setCompletedCreateIds] = useState<
    Map<PendingItemId, number>
  >(() => new Map());

  type CreateMutationVars = {
    encounterId: number;
    data: { description: string };
  };

  type CreateContext =
    | { kind: "noop" }
    | {
        kind: "optimistic";
        previous: CachedAgendaItemList | undefined;
        tempId: PendingItemId;
      };

  const { mutateAsync: createMutation } = useCreateAgendaItem<
    CreateAgendaItemMutationError,
    CreateContext
  >({
    mutation: {
      onMutate: async (vars: CreateMutationVars): Promise<CreateContext> => {
        if (patientId == null) {
          return { kind: "noop" };
        }

        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        await queryClient.cancelQueries({ queryKey });

        const previous =
          queryClient.getQueryData<CachedAgendaItemList>(queryKey);

        const tempId = mintPendingId();
        setPendingCreateIds((ids) => [...ids, tempId]);
        const now = new Date().toISOString();
        const placeholder: AgendaItem = {
          id: tempId,
          description: vars.data.description,
          type: "custom",
          status: "open",
          signalState: "open",
          patientId,
          practiceId,
          encounterId: vars.encounterId,
          createdAt: now,
          updatedAt: now,
        };

        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => ({
          ...(old ?? {}),
          data: {
            items: [...(old?.data?.items ?? []), placeholder],
          },
        }));

        return { kind: "optimistic", previous, tempId };
      },
      onSuccess: (envelope, vars, context) => {
        if (!context || context.kind === "noop") return;

        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        const serverItem = envelope.data;
        const { tempId } = context;

        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => {
          const items = old?.data?.items ?? [];
          const realAlreadyPresent = items.some((i) => i.id === serverItem.id);

          if (realAlreadyPresent) {
            return {
              ...(old ?? {}),
              data: { items: items.filter((i) => i.id !== tempId) },
            };
          }

          const placeholderPresent = items.some((i) => i.id === tempId);
          const next = placeholderPresent
            ? items.map((i) => (i.id === tempId ? serverItem : i))
            : [...items, serverItem];

          return {
            ...(old ?? {}),
            data: { items: next },
          };
        });
        setError(null);
        setPendingCreateIds((ids) => ids.filter((id) => id !== tempId));
        setCompletedCreateIds((ids) => {
          const next = new Map(ids);
          next.set(tempId, serverItem.id);
          return next;
        });
      },
      onError: (err, vars, context) => {
        console.error("[AgendaV2] create agenda item failed", {
          encounterId: vars.encounterId,
          tempId: context?.kind === "optimistic" ? context.tempId : undefined,
          description: vars.data.description,
          error: err,
        });
        setError({ verb: "create", status: err.response?.status ?? null });
        if (!context || context.kind === "noop") return;

        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        queryClient.setQueryData(queryKey, context.previous);
        setPendingCreateIds((ids) => ids.filter((id) => id !== context.tempId));
        setCompletedCreateIds((ids) => {
          if (!ids.has(context.tempId)) return ids;
          const next = new Map(ids);
          next.delete(context.tempId);
          return next;
        });
      },
    },
  });

  type UpdateMutationVars = {
    encounterId: number;
    itemId: number;
    data: { status?: AgendaItemStatus };
  };

  type UpdateContext = {
    previous: CachedAgendaItemList | undefined;
  };

  const { mutateAsync: updateMutation } = useUpdateAgendaItem<
    UpdateAgendaItemMutationError,
    UpdateContext
  >({
    mutation: {
      onMutate: async (vars: UpdateMutationVars): Promise<UpdateContext> => {
        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        await queryClient.cancelQueries({ queryKey });

        const previous =
          queryClient.getQueryData<CachedAgendaItemList>(queryKey);

        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => ({
          ...(old ?? {}),
          data: {
            items: (old?.data?.items ?? []).map((i) =>
              i.id === vars.itemId
                ? { ...i, ...getOptimisticAgendaItemPatch(vars.data) }
                : i,
            ),
          },
        }));

        return { previous };
      },
      onSuccess: (envelope, vars) => {
        setError(null);

        const serverItem = envelope.data;
        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);

        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => ({
          ...(old ?? {}),
          data: {
            items: (old?.data?.items ?? []).map((i) =>
              i.id === vars.itemId ? serverItem : i,
            ),
          },
        }));
      },
      onError: (err, vars, context) => {
        console.error("[AgendaV2] update agenda item failed", {
          encounterId: vars.encounterId,
          itemId: vars.itemId,
          status: vars.data.status,
          error: err,
        });
        setError({ verb: "update", status: err.response?.status ?? null });
        if (!context) return;

        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        queryClient.setQueryData(queryKey, context.previous);
      },
    },
  });

  type DeleteMutationVars = {
    encounterId: number;
    itemId: number;
  };

  type DeleteContext = {
    previous: CachedAgendaItemList | undefined;
  };

  const { mutateAsync: deleteMutation } = useDeleteAgendaItem<
    DeleteAgendaItemMutationError,
    DeleteContext
  >({
    mutation: {
      onMutate: async (vars: DeleteMutationVars): Promise<DeleteContext> => {
        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        await queryClient.cancelQueries({ queryKey });

        const previous =
          queryClient.getQueryData<CachedAgendaItemList>(queryKey);

        queryClient.setQueryData<CachedAgendaItemList>(queryKey, (old) => ({
          ...(old ?? {}),
          data: {
            items: (old?.data?.items ?? []).filter((i) => i.id !== vars.itemId),
          },
        }));

        return { previous };
      },
      onSuccess: (_data, vars) => {
        setError(null);
        setCompletedCreateIds((ids) => {
          const entries = [...ids.entries()].filter(
            ([, serverId]) => serverId !== vars.itemId,
          );

          return entries.length === ids.size ? ids : new Map(entries);
        });
      },
      onError: (err, vars, context) => {
        console.error("[AgendaV2] delete agenda item failed", {
          encounterId: vars.encounterId,
          itemId: vars.itemId,
          error: err,
        });
        setError({ verb: "delete", status: err.response?.status ?? null });
        if (!context) return;

        const queryKey = getListAgendaItemsQueryKey(vars.encounterId);
        queryClient.setQueryData(queryKey, context.previous);
      },
    },
  });

  const createAgendaItem = async (description: string) => {
    if (encounterId == null || patientId == null) return;
    await createMutation({ encounterId, data: { description } });
  };

  const toggleAgendaItem = async (
    itemId: number,
    currentStatus: AgendaItemStatus,
    currentSignalState: AgendaItemSignalState,
  ) => {
    if (encounterId == null) return;

    const isCompleted =
      currentStatus === "finalized" || currentSignalState === "planned";
    const nextStatus: AgendaItemStatus = isCompleted ? "open" : "finalized";

    if (
      nextStatus === currentStatus &&
      !(nextStatus === "open" && currentSignalState !== "open")
    ) {
      return;
    }

    await updateMutation({ encounterId, itemId, data: { status: nextStatus } });
  };

  const deleteAgendaItem = async (itemId: number) => {
    if (encounterId == null) return;
    await deleteMutation({ encounterId, itemId });
  };

  return {
    createAgendaItem,
    toggleAgendaItem,
    deleteAgendaItem,
    error,
    pendingCreateIds,
    completedCreateIds,
  };
}
